import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ProCogia Brand Colors (from the email you shared)
export const procogiaBrand = {
  // Primary ProCogia Green
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#95c100', // Main ProCogia Green #95c100
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d'
  },
  
  // ProCogia Blue
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#1fb8cc', // ProCogia Blue #1fb8cc
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a'
  },
  
  // ProCogia Dark Gray
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#333333', // ProCogia Dark Gray #333333
    900: '#111827'
  }
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    // Check localStorage first, then system preference
    const stored = localStorage.getItem('theme');
    if (stored) {
      return stored === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Apply theme to document
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.style.setProperty('--color-primary', procogiaBrand.green[500]);
      document.documentElement.style.setProperty('--color-secondary', procogiaBrand.blue[500]);
      document.documentElement.style.setProperty('--color-accent', procogiaBrand.gray[800]);
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.setProperty('--color-primary', procogiaBrand.green[500]);
      document.documentElement.style.setProperty('--color-secondary', procogiaBrand.blue[500]);
      document.documentElement.style.setProperty('--color-accent', procogiaBrand.gray[100]);
    }
    
    // Save to localStorage
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  const getThemeClasses = () => {
    return {
      // Background classes
      bg: {
        primary: isDark ? 'bg-gray-900' : 'bg-gray-50',
        secondary: isDark ? 'bg-gray-800' : 'bg-white',
        accent: isDark ? 'bg-gray-700' : 'bg-gray-100',
        card: isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      },
      
      // Text classes
      text: {
        primary: isDark ? 'text-white' : 'text-gray-900',
        secondary: isDark ? 'text-gray-300' : 'text-gray-600',
        accent: isDark ? 'text-gray-400' : 'text-gray-500',
        inverse: isDark ? 'text-gray-900' : 'text-white'
      },
      
      // ProCogia brand colors
      brand: {
        green: 'bg-procogia-green text-white',
        blue: 'bg-procogia-blue text-white',
        greenText: 'text-procogia-green',
        blueText: 'text-procogia-blue'
      },
      
      // Button classes
      button: {
        primary: `bg-procogia-green hover:bg-opacity-90 text-white`,
        secondary: `bg-procogia-blue hover:bg-opacity-90 text-white`,
        accent: isDark 
          ? 'bg-gray-700 hover:bg-gray-600 text-white border-gray-600' 
          : 'bg-white hover:bg-gray-50 text-gray-900 border-gray-300'
      },
      
      // Border classes
      border: {
        primary: isDark ? 'border-gray-700' : 'border-gray-200',
        accent: isDark ? 'border-gray-600' : 'border-gray-300'
      }
    };
  };

  const value = {
    isDark,
    toggleTheme,
    getThemeClasses,
    procogiaBrand
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 